import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpenCheck, Plus, Pencil, Trash2, Printer, FileSpreadsheet, Search, Loader2, Eye, X, FileDown } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { listLessonPlans, deleteLessonPlan } from "../lib/api";
import { STAGES, STAGE_KEYS, GRADES, GRADES_BY_STAGE, sectionsForGrade, can, PLAN_DAYS, PLAN_PERIODS } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import { exportToExcel } from "../lib/excel";
import { SCHOOL_LOGO, SCHOOL_NAME } from "../lib/constants";

export default function LessonPlansList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = can.writeLessonPlan(user);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const [stage, setStage] = useState("all");
  const [grade, setGrade] = useState("all");
  const [section, setSection] = useState("all");
  const [weekFrom, setWeekFrom] = useState("");
  const [weekTo, setWeekTo] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const grades = stage !== "all" ? GRADES_BY_STAGE[stage] : GRADES;
  const sections = grade !== "all" ? sectionsForGrade(grade) : [];

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (stage !== "all") params.stage = stage;
      if (grade !== "all") params.grade = grade;
      if (section !== "all") params.section = section;
      if (weekFrom) params.week_from = weekFrom;
      if (weekTo) params.week_to = weekTo;
      setRows(await listLessonPlans(params));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التحميل");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onDelete = async () => {
    try {
      await deleteLessonPlan(deleting.id);
      toast.success("تم حذف الخطة");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحذف");
    }
  };

  const teachersOf = (plan) => {
    const names = new Set();
    Object.values(plan.cells || {}).forEach(c => { if (c?.teacher_name) names.add(c.teacher_name); });
    return Array.from(names);
  };

  const handleExcel = () => {
    if (!rows.length) { toast.info("لا توجد خطط للتصدير"); return; }
    exportToExcel(`خطط_دراسية_${new Date().toISOString().slice(0, 10)}.xlsx`, [{
      name: "ملخص الخطط",
      columns: [
        { key: "teachers", header: "المعلمون المشاركون" },
        { key: "stage_label", header: "المرحلة" },
        { key: "grade", header: "الصف" },
        { key: "section", header: "الفصل" },
        { key: "week_start", header: "بداية الأسبوع" },
        { key: "week_end", header: "نهاية الأسبوع" },
        { key: "academic_year", header: "العام الدراسي" },
      ],
      rows: rows.map(r => ({ ...r, stage_label: STAGES[r.stage] || r.stage, teachers: teachersOf(r).join("، ") })),
    }]);
    toast.success("تم تنزيل ملف Excel");
  };

  const handleSavePdf = async (plan) => {
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.top = "-99999px";
    container.style.left = "-99999px";
    container.style.width = "1400px";
    container.style.background = "#fff";
    container.style.padding = "24px";
    container.style.fontFamily = "'Tajawal','Cairo',sans-serif";
    container.dir = "rtl";

    const today = new Date().toLocaleDateString("ar-SA-u-nu-latn");
    const rowsHtml = PLAN_DAYS.map(d => {
      const tds = PLAN_PERIODS.map(p => {
        const c = plan.cells?.[`${d.key}_${p.key}`] || {};
        const lesson = c.lesson || "";
        const homework = c.homework ? `<div style="color:#92400E;font-size:11px;margin-top:3px"><strong>واجب:</strong> ${c.homework}</div>` : "";
        const teacher = c.teacher_name ? `<div style="color:#0F766E;font-size:10px;margin-top:2px">${c.teacher_name}</div>` : "";
        return `<td style="border:1px solid #CBD5E1;padding:8px;vertical-align:top;width:13%;font-size:12px">${lesson}${homework}${teacher}</td>`;
      }).join("");
      return `<tr><td style="border:1px solid #CBD5E1;padding:8px;background:#F0FDFA;text-align:center;font-weight:700;color:#115E59;font-family:'Cairo',sans-serif">${d.label}</td>${tds}</tr>`;
    }).join("");
    const periodsHtml = PLAN_PERIODS.map(p => `<th style="border:1px solid #CBD5E1;background:#0F766E;color:white;padding:10px;font-family:'Cairo',sans-serif;font-size:13px">${p.label}</th>`).join("");

    container.innerHTML = `
      <div style="display:flex;gap:14px;align-items:center;border-bottom:2px solid #0F766E;padding-bottom:12px;margin-bottom:14px">
        <img src="${SCHOOL_LOGO}" style="width:64px;height:64px;object-fit:contain" />
        <div><div style="font-family:'Cairo',sans-serif;font-size:18px;font-weight:800;color:#115E59">${SCHOOL_NAME}</div><div style="color:#475569;font-size:12px;margin-top:2px">نظام إدارة الطلاب — الخطط الدراسية</div></div>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center">
        <h1 style="font-family:'Cairo',sans-serif;font-size:20px;color:#0F766E;margin:4px 0">الخطة الأسبوعية — ${plan.grade} / ${plan.section}</h1>
        <div style="color:#0F766E;font-weight:700">تاريخ الطباعة: ${today}</div>
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:18px;margin:14px 0;font-size:13px;color:#334155">
        <div><span style="color:#64748B">المرحلة:</span> <strong>${STAGES[plan.stage] || ""}</strong></div>
        <div><span style="color:#64748B">الصف:</span> <strong>${plan.grade}</strong></div>
        <div><span style="color:#64748B">الفصل:</span> <strong>${plan.section}</strong></div>
        <div><span style="color:#64748B">الأسبوع:</span> <strong>${plan.week_start} → ${plan.week_end}</strong></div>
        <div><span style="color:#64748B">العام:</span> <strong>${plan.academic_year}</strong></div>
      </div>
      <table style="width:100%;border-collapse:collapse;margin-top:8px">
        <thead><tr><th style="border:1px solid #CBD5E1;background:#0F766E;color:white;padding:10px;font-family:'Cairo',sans-serif;font-size:13px">اليوم / الحصة</th>${periodsHtml}</tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div style="margin-top:16px;display:flex;justify-content:space-between;color:#64748B;font-size:12px;border-top:1px dashed #CBD5E1;padding-top:8px">
        <div>تم إنشاء الخطة بواسطة نظام إدارة الطلاب</div><div>© ${new Date().getFullYear()} — ${SCHOOL_NAME}</div>
      </div>`;

    document.body.appendChild(container);
    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW - 16;
      const imgH = (canvas.height * imgW) / canvas.width;
      const finalH = Math.min(imgH, pageH - 16);
      pdf.addImage(imgData, "PNG", 8, 8, imgW, finalH);
      pdf.save(`خطة_اسبوعية_${plan.grade}_${plan.section}_${plan.week_start}.pdf`);
    } catch (e) {
      toast.error("تعذر إنشاء ملف PDF");
    } finally {
      document.body.removeChild(container);
    }
  };

  const handlePrintPlan = (plan) => {
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) return;
    const today = new Date().toLocaleDateString("ar-SA-u-nu-latn");
    const rowsHtml = PLAN_DAYS.map(d => {
      const tds = PLAN_PERIODS.map(p => {
        const c = plan.cells?.[`${d.key}_${p.key}`] || {};
        const lesson = c.lesson || "";
        const homework = c.homework ? `<div style="color:#92400E;font-size:10px;margin-top:3px"><strong>واجب:</strong> ${c.homework}</div>` : "";
        const teacher = c.teacher_name ? `<div style="color:#0F766E;font-size:9px;margin-top:2px">${c.teacher_name}</div>` : "";
        return `<td style="border:1px solid #CBD5E1;padding:6px;vertical-align:top;width:13%;font-size:11px"><div>${lesson}</div>${homework}${teacher}</td>`;
      }).join("");
      return `<tr><td style="border:1px solid #CBD5E1;padding:6px;background:#F0FDFA;text-align:center;font-weight:700;color:#115E59;font-family:'Cairo',sans-serif">${d.label}</td>${tds}</tr>`;
    }).join("");
    const periodsHtml = PLAN_PERIODS.map(p => `<th style="border:1px solid #CBD5E1;background:#0F766E;color:white;padding:8px;font-family:'Cairo',sans-serif;font-size:12px">${p.label}</th>`).join("");

    w.document.write(`<!doctype html><html lang=\"ar\" dir=\"rtl\"><head><meta charset=\"utf-8\"><title>خطة أسبوعية - ${plan.grade} ${plan.section}</title>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@500;700;800&family=Tajawal:wght@400;500;700&display=swap" rel="stylesheet">
      <style>body{font-family:'Tajawal','Cairo',sans-serif;padding:24px;color:#0F172A}
        .head{display:flex;gap:14px;align-items:center;border-bottom:2px solid #0F766E;padding-bottom:12px;margin-bottom:14px}
        .head img{width:64px;height:64px;object-fit:contain}
        .school{font-family:'Cairo',sans-serif;font-size:18px;font-weight:800;color:#115E59}
        h1{font-family:'Cairo',sans-serif;font-size:20px;color:#0F766E;margin:4px 0}
        .meta{display:flex;flex-wrap:wrap;gap:18px;margin:14px 0;font-size:12px;color:#334155}
        .meta span{color:#64748B}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        .footer{margin-top:16px;display:flex;justify-content:space-between;color:#64748B;font-size:11px;border-top:1px dashed #CBD5E1;padding-top:8px}
        .credits{margin-top:6px;display:flex;flex-direction:column;align-items:center;gap:4px;color:#0F766E;font-size:11px;font-family:'Cairo',sans-serif;border-top:1px solid #E2E8F0;padding-top:6px}
        @page{size:landscape}@media print{body{padding:10mm}}
      </style></head><body>
      <div class="head"><img src="${SCHOOL_LOGO}"/><div><div class="school">${SCHOOL_NAME}</div><div style="color:#475569;font-size:12px;margin-top:2px">نظام إدارة الطلاب — الخطط الدراسية</div></div></div>
      <div style="display:flex;justify-content:space-between;align-items:center"><h1>الخطة الأسبوعية — ${plan.grade} / ${plan.section}</h1><div style="color:#0F766E;font-weight:700">تاريخ الطباعة: ${today}</div></div>
      <div class="meta">
        <div><span>المرحلة:</span> <strong>${STAGES[plan.stage] || ""}</strong></div>
        <div><span>الصف:</span> <strong>${plan.grade}</strong></div>
        <div><span>الفصل:</span> <strong>${plan.section}</strong></div>
        <div><span>الأسبوع:</span> <strong>${plan.week_start} → ${plan.week_end}</strong></div>
        <div><span>العام:</span> <strong>${plan.academic_year}</strong></div>
      </div>
      <table><thead><tr><th style="border:1px solid #CBD5E1;background:#0F766E;color:white;padding:8px;font-family:'Cairo',sans-serif;font-size:12px">اليوم / الحصة</th>${periodsHtml}</tr></thead><tbody>${rowsHtml}</tbody></table>
      <div class="footer"><div>تم إنشاء الخطة بواسطة نظام إدارة الطلاب</div><div>© ${new Date().getFullYear()} — ${SCHOOL_NAME}</div></div>
      <div class="credits"><div>إدارة المدرسة: <strong>الأستاذ محمد عبدالله الصبحي</strong></div><div>برمجة وتصميم: <strong>الأستاذ محمد كامل</strong> • <span dir="ltr">0590939015</span></div></div>
      <script>window.onload=()=>setTimeout(()=>window.print(),350)</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div>
      <PageHeader
        title="الخطط الدراسية الأسبوعية"
        subtitle="خطة واحدة مشتركة لكل فصل — كل معلم يُدخل بيانات حصته الخاصة"
        icon={BookOpenCheck}
        actions={canWrite ? (
          <Button onClick={() => nav("/lesson-plans/new")} className="bg-teal-700 hover:bg-teal-800 text-white gap-2 btn-elevate" data-testid="btn-new-plan">
            <Plus size={16} /> خطة جديدة / إكمال خطة
          </Button>
        ) : null}
      />

      <Card className="p-4 sm:p-5 border-slate-200 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label>المرحلة</Label>
            <Select value={stage} onValueChange={(v) => { setStage(v); setGrade("all"); setSection("all"); }}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">جميع المراحل</SelectItem>
                {STAGE_KEYS.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الصف</Label>
            <Select value={grade} onValueChange={(v) => { setGrade(v); setSection("all"); }}>
              <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">جميع الصفوف</SelectItem>
                {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {grade !== "all" && (
            <div className="space-y-1.5">
              <Label>الفصل</Label>
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger className="h-10" dir="rtl"><SelectValue /></SelectTrigger>
                <SelectContent dir="rtl">
                  <SelectItem value="all">جميع الفصول</SelectItem>
                  {sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>من تاريخ</Label>
            <Input type="date" value={weekFrom} onChange={(e) => setWeekFrom(e.target.value)} className="h-10" />
          </div>
          <div className="space-y-1.5">
            <Label>إلى تاريخ</Label>
            <Input type="date" value={weekTo} onChange={(e) => setWeekTo(e.target.value)} className="h-10" />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button onClick={handleExcel} variant="outline" className="h-10 gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-lp-excel">
            <FileSpreadsheet size={15} /> تصدير Excel
          </Button>
          <Button onClick={load} disabled={loading} className="h-10 px-5 bg-teal-700 hover:bg-teal-800 text-white gap-2 btn-elevate" data-testid="btn-lp-search">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
            بحث
          </Button>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm min-w-[720px]" data-testid="lp-table">
            <thead>
              <tr className="bg-teal-700 text-white">
                <th className="px-2 sm:px-3 py-2.5 text-center font-cairo w-12">#</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo">المعلمون المشاركون</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo">المرحلة</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo">الصف</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo">الفصل</th>
                <th className="px-2 sm:px-3 py-2.5 text-right font-cairo whitespace-nowrap">الأسبوع</th>
                <th className="px-2 sm:px-3 py-2.5 text-center font-cairo w-32">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400"><Loader2 className="animate-spin inline mx-2" size={18} /> جاري التحميل...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400">لا توجد خطط مطابقة. {canWrite && <Link to="/lesson-plans/new" className="text-teal-700 underline">إنشاء خطة جديدة</Link>}</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.id} className="odd:bg-white even:bg-slate-50/60 hover:bg-teal-50/50">
                  <td className="px-2 sm:px-3 py-2 text-center text-slate-500 tabular-nums">{i + 1}</td>
                  <td className="px-2 sm:px-3 py-2 text-slate-800">{teachersOf(r).join("، ") || "—"}</td>
                  <td className="px-2 sm:px-3 py-2 text-slate-700">{STAGES[r.stage] || "—"}</td>
                  <td className="px-2 sm:px-3 py-2 text-slate-700 whitespace-nowrap">{r.grade}</td>
                  <td className="px-2 sm:px-3 py-2 text-slate-700 whitespace-nowrap">{r.section}</td>
                  <td className="px-2 sm:px-3 py-2 text-slate-700 tabular-nums whitespace-nowrap" dir="ltr">{r.week_start} → {r.week_end}</td>
                  <td className="px-2 sm:px-3 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setViewing(r)} className="h-8 w-8 p-0 text-slate-700 hover:bg-slate-100" data-testid={`lp-view-${r.id}`} title="عرض">
                        <Eye size={15} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handlePrintPlan(r)} className="h-8 w-8 p-0 text-teal-700 hover:bg-teal-100" title="طباعة" data-testid={`lp-print-${r.id}`}>
                        <Printer size={15} />
                      </Button>

                      {canWrite && (
                        <Button size="sm" variant="ghost" onClick={() => nav(`/lesson-plans/${r.id}/edit`)} className="h-8 w-8 p-0 text-amber-700 hover:bg-amber-100" title="تعديل / إضافة حصتي" data-testid={`lp-edit-${r.id}`}>
                          <Pencil size={15} />
                        </Button>
                      )}
                      {isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => setDeleting(r)} className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-100" title="حذف الخطة كاملة" data-testid={`lp-delete-${r.id}`}>
                          <Trash2 size={15} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500 bg-slate-50/60">
          إجمالي الخطط: <span className="font-bold text-slate-700 tabular-nums">{rows.length}</span>
        </div>
      </Card>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-cairo">
              {viewing?.grade} / {viewing?.section}
              <div className="text-xs text-slate-500 mt-1 font-normal">{viewing?.week_start} → {viewing?.week_end}</div>
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm border-collapse">
                <thead>
                  <tr className="bg-teal-700 text-white">
                    <th className="px-2 py-2 border border-teal-800 font-cairo w-24">اليوم / الحصة</th>
                    {PLAN_PERIODS.map(p => <th key={p.key} className="px-2 py-2 border border-teal-800 font-cairo">{p.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {PLAN_DAYS.map((d, di) => (
                    <tr key={d.key} className={di % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                      <td className="border border-slate-200 text-center font-cairo font-bold text-teal-800 bg-teal-50/50 px-2 py-2">{d.label}</td>
                      {PLAN_PERIODS.map(p => {
                        const c = viewing.cells?.[`${d.key}_${p.key}`] || {};
                        return (
                          <td key={p.key} className="border border-slate-200 px-2 py-2 align-top">
                            {c.lesson && <div className="text-slate-800">{c.lesson}</div>}
                            {c.homework && <div className="text-amber-700 text-[11px] mt-1"><strong>واجب:</strong> {c.homework}</div>}
                            {c.teacher_name && <div className="text-teal-700 text-[10px] mt-1">{c.teacher_name}</div>}
                            {!c.lesson && !c.homework && <div className="text-slate-300">—</div>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setViewing(null)} className="gap-1"><X size={15} /> إغلاق</Button>
                <Button onClick={() => handlePrintPlan(viewing)} className="bg-teal-700 hover:bg-teal-800 text-white gap-1"><Printer size={15} /> طباعة</Button>

              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-cairo">حذف الخطة بالكامل</AlertDialogTitle>
            <AlertDialogDescription>هل تريد حذف خطة <strong>{deleting?.grade} / {deleting?.section}</strong> لأسبوع {deleting?.week_start}؟ سيتم حذف إدخالات جميع المعلمين في هذه الخطة.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
